import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { MoleculeFacts, PublishedStatement } from '@peruselab/api-client/molecules';
import { SafetyFacts } from '../chemistry/molecule/SafetyFacts';
import { StatementAdder, useStatementEditing } from '../chemistry/molecule/StatementForm';
import { StatementEditing } from '../chemistry/molecule/statementEditing';

/**
 * #947 phase 2: Safety -- GHS classification and toxicity (ADR 0017 #908) -- with "Add a toxicity
 * note" below, and Correct / Withdraw on your own notes.
 */
export function MonographSafety({
  moleculeId,
  facts,
  toxicity,
  onChanged
}: {
  readonly moleculeId: string;
  readonly facts: MoleculeFacts | null;
  readonly toxicity: readonly PublishedStatement[];
  readonly onChanged: () => void;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const { editing, correcting, done } = useStatementEditing(onChanged);
  const hasClassification = facts?.status === 'ready' && (facts.ghs?.length ?? 0) > 0;
  return (
    <StatementEditing.Provider value={editing}>
      {hasClassification || toxicity.length > 0 ? (
        <SafetyFacts
          facts={
            facts?.status === 'ready' ? facts : ({ status: 'ready', ghs: [], toxicity: [] } as unknown as MoleculeFacts)
          }
          toxicity={toxicity}
        />
      ) : (
        <section className="published-facts-section" aria-labelledby={headingId}>
          <h2 id={headingId}>{t('moleculePage.safety')}</h2>
          <p className="discussion-note">{t('statements.none.toxicity-summary')}</p>
        </section>
      )}
      <StatementAdder
        moleculeId={moleculeId}
        propertyId="toxicity-summary"
        correcting={correcting}
        onCorrectDone={done}
        onChanged={onChanged}
      />
    </StatementEditing.Provider>
  );
}
